import { MessageContextItemKind } from '@affine/graphql';

import type { MailMessageAttachment } from '../../utils/mail-selection-context';
import type {
  AutomationAttachment,
  CanvasAIChatContextMode,
  CanvasSelectionAttachment,
  CanvasSelectionItem,
  CanvasSelectionPriority,
  CanvasSelectionSource,
  CodeSelectionAttachment,
  DatabaseAttachment,
  DatabaseAttachmentPriority,
  DatabaseAttachmentSource,
  PdfContextAttachment,
  WorkflowAttachment,
} from '../ai-chat-content/type';
import type { ChatMessage } from '../ai-chat-messages';
import type { JsonValue } from '../ai-chat-messages/type';
import type { CanonicalMessageContextItem } from './canonical-message-context';
import type { SelectedContextChip } from './type';

const CONTEXT_SOURCES: Record<string, DatabaseAttachmentSource> = {
  FOCUS: 'focus',
  focus: 'focus',
  FULLSCREEN: 'fullscreen',
  fullscreen: 'fullscreen',
  PASTE: 'paste',
  paste: 'paste',
};

const CANVAS_CONTEXT_SOURCES: Record<string, CanvasSelectionSource> = {
  ...CONTEXT_SOURCES,
  CLIENT_ACTION: 'client-action',
  'client-action': 'client-action',
};

const CONTEXT_PRIORITIES: Record<string, DatabaseAttachmentPriority> = {
  HIGHEST: 'highest',
  highest: 'highest',
  MID: 'mid',
  mid: 'mid',
  LOWEST: 'lowest',
  lowest: 'lowest',
};

const CANVAS_CONTEXT_PRIORITIES: Record<string, CanvasSelectionPriority> = {
  ...CONTEXT_PRIORITIES,
  high: 'high',
};

function visibleCanvasSelectionItems(
  items: CanvasSelectionItem[],
  aiChatContextMode: CanvasAIChatContextMode
): CanvasSelectionItem[] {
  return items.flatMap(item => {
    if (
      item.type === 'affine:embed-ai-chat' &&
      (aiChatContextMode === 'lineage-only' || !item.text)
    ) {
      return [];
    }
    const children = item.children?.length
      ? visibleCanvasSelectionItems(item.children, aiChatContextMode)
      : undefined;
    return [
      {
        ...item,
        children: children?.length ? children : undefined,
        childCount: children?.length ?? 0,
      },
    ];
  });
}

function flattenCanvasSelectionItems(
  items: CanvasSelectionItem[]
): CanvasSelectionItem[] {
  return items.flatMap(item => [
    item,
    ...flattenCanvasSelectionItems(item.children ?? []),
  ]);
}

const MAIL_CONTEXT_SOURCES: Record<string, MailMessageAttachment['source']> = {
  FULLSCREEN: 'fullscreen',
  fullscreen: 'fullscreen',
};

const MAIL_CONTEXT_PRIORITIES: Record<
  string,
  MailMessageAttachment['priority']
> = {
  HIGHEST: 'highest',
  highest: 'highest',
  MID: 'mid',
  mid: 'mid',
};

function contextItems(
  item: ChatMessage,
  kind: MessageContextItemKind
): CanonicalMessageContextItem[] {
  const items = item.context?.items;
  if (!Array.isArray(items)) return [];
  return items.filter(
    contextItem => contextItem.kind === kind
  ) as CanonicalMessageContextItem[];
}

const GENERIC_CONTEXT_KINDS = new Set<MessageContextItemKind>([
  MessageContextItemKind.DOC,
  MessageContextItemKind.TAG,
  MessageContextItemKind.COLLECTION,
  MessageContextItemKind.IMAGE,
  MessageContextItemKind.PDF_FILE,
  MessageContextItemKind.FILE,
  MessageContextItemKind.ATTACHMENT,
  MessageContextItemKind.ASSISTANT_CHANNEL,
]);

export function genericContextItemsFromMessage(
  item: ChatMessage
): CanonicalMessageContextItem[] {
  const items = item.context?.items;
  if (!Array.isArray(items)) return [];
  return items.filter(contextItem => {
    if (GENERIC_CONTEXT_KINDS.has(contextItem.kind as MessageContextItemKind)) {
      return true;
    }
    return !canHydrateSpecializedContextItem(
      contextItem as CanonicalMessageContextItem
    );
  }) as CanonicalMessageContextItem[];
}

function payloadRecord(
  item: CanonicalMessageContextItem
): Record<string, JsonValue> | null {
  const payload = item.payload;
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
    return null;
  }
  return payload;
}

function canvasSourceFromContext(
  source: unknown
): CanvasSelectionSource | null {
  return typeof source === 'string'
    ? (CANVAS_CONTEXT_SOURCES[source] ?? null)
    : null;
}

function canvasPriorityFromContext(
  priority: unknown
): CanvasSelectionPriority | null {
  return typeof priority === 'string'
    ? (CANVAS_CONTEXT_PRIORITIES[priority] ?? null)
    : null;
}

function mailSourceFromContext(
  source: unknown
): MailMessageAttachment['source'] | null {
  return typeof source === 'string'
    ? (MAIL_CONTEXT_SOURCES[source] ?? null)
    : null;
}

function mailPriorityFromContext(
  priority: unknown
): MailMessageAttachment['priority'] | null {
  return typeof priority === 'string'
    ? (MAIL_CONTEXT_PRIORITIES[priority] ?? null)
    : null;
}

function contextSourceFromContext(
  source: unknown
): DatabaseAttachmentSource | null {
  return typeof source === 'string' ? (CONTEXT_SOURCES[source] ?? null) : null;
}

function contextPriorityFromContext(
  priority: unknown
): DatabaseAttachmentPriority | null {
  return typeof priority === 'string'
    ? (CONTEXT_PRIORITIES[priority] ?? null)
    : null;
}

function hasString(value: unknown): value is string {
  return typeof value === 'string' && value.length > 0;
}

function hasNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function canHydrateSpecializedContextItem(
  contextItem: CanonicalMessageContextItem
): boolean {
  const payload = payloadRecord(contextItem);
  switch (contextItem.kind) {
    case MessageContextItemKind.DATABASE: {
      const source = contextSourceFromContext(contextItem.source);
      const priority = contextPriorityFromContext(contextItem.priority);
      const value = payload as unknown as DatabaseAttachment | null;
      return Boolean(
        payload &&
        source &&
        priority &&
        hasString(value?.blockId) &&
        hasString(value?.name) &&
        Array.isArray(value?.columns)
      );
    }
    case MessageContextItemKind.AUTOMATION: {
      const source = contextSourceFromContext(contextItem.source);
      const priority = contextPriorityFromContext(contextItem.priority);
      const value = payload as unknown as AutomationAttachment | null;
      return Boolean(
        payload &&
        source &&
        priority &&
        hasString(value?.blockId) &&
        hasString(value?.name)
      );
    }
    case MessageContextItemKind.WORKFLOW: {
      const source = contextSourceFromContext(contextItem.source);
      const priority = contextPriorityFromContext(contextItem.priority);
      const value = payload as unknown as WorkflowAttachment | null;
      return Boolean(
        payload &&
        source &&
        priority &&
        hasString(value?.workflowId) &&
        Array.isArray(value?.selectedNodes) &&
        Array.isArray(value?.haloNodes) &&
        Array.isArray(value?.edges) &&
        hasNumber(value?.tokenEstimate)
      );
    }
    case MessageContextItemKind.CODE_SELECTION: {
      const source = contextSourceFromContext(contextItem.source);
      const priority = contextPriorityFromContext(contextItem.priority);
      const value = payload as unknown as CodeSelectionAttachment | null;
      return Boolean(
        payload &&
        source &&
        priority &&
        hasString(value?.id) &&
        hasString(value?.title) &&
        hasString(value?.surface) &&
        hasString(value?.language) &&
        hasNumber(value?.fromLine) &&
        hasNumber(value?.toLine)
      );
    }
    case MessageContextItemKind.PDF_BLOCK: {
      const source = contextSourceFromContext(contextItem.source);
      const priority = contextPriorityFromContext(contextItem.priority);
      const value = payload as unknown as PdfContextAttachment | null;
      return Boolean(
        payload &&
        source &&
        priority &&
        hasString(value?.docId) &&
        hasString(value?.blockId) &&
        hasString(value?.name)
      );
    }
    case MessageContextItemKind.CANVAS_SELECTION: {
      const source = canvasSourceFromContext(contextItem.source);
      const priority = canvasPriorityFromContext(contextItem.priority);
      const value = payload as unknown as CanvasSelectionAttachment | null;
      return Boolean(
        payload &&
        source &&
        priority &&
        hasString(value?.docId) &&
        hasString(value?.label) &&
        Array.isArray(value?.elementIds) &&
        Array.isArray(value?.blockIds) &&
        Array.isArray(value?.items)
      );
    }
    case MessageContextItemKind.MAIL: {
      const source = mailSourceFromContext(contextItem.source);
      const priority = mailPriorityFromContext(contextItem.priority);
      const value = payload as unknown as MailMessageAttachment | null;
      return Boolean(payload && source && priority && hasString(value?.id));
    }
    default:
      return false;
  }
}

export function databaseAttachmentsFromMessage(
  item: ChatMessage
): DatabaseAttachment[] {
  return contextItems(item, MessageContextItemKind.DATABASE).flatMap(
    contextItem => {
      const payload = payloadRecord(contextItem);
      const source = contextSourceFromContext(contextItem.source);
      const priority = contextPriorityFromContext(contextItem.priority);
      if (!payload || !source || !priority) return [];

      const value = payload as unknown as DatabaseAttachment;
      if (
        !hasString(value.blockId) ||
        !hasString(value.name) ||
        !Array.isArray(value.columns)
      ) {
        return [];
      }
      return [{ ...value, source, priority }];
    }
  );
}

export function automationAttachmentsFromMessage(
  item: ChatMessage
): AutomationAttachment[] {
  return contextItems(item, MessageContextItemKind.AUTOMATION).flatMap(
    contextItem => {
      const payload = payloadRecord(contextItem);
      const source = contextSourceFromContext(contextItem.source);
      const priority = contextPriorityFromContext(contextItem.priority);
      if (!payload || !source || !priority) return [];

      const value = payload as unknown as AutomationAttachment;
      if (!hasString(value.blockId) || !hasString(value.name)) {
        return [];
      }
      return [{ ...value, source, priority }];
    }
  );
}

export function workflowAttachmentsFromMessage(
  item: ChatMessage
): WorkflowAttachment[] {
  return contextItems(item, MessageContextItemKind.WORKFLOW).flatMap(
    contextItem => {
      const payload = payloadRecord(contextItem);
      const source = contextSourceFromContext(contextItem.source);
      const priority = contextPriorityFromContext(contextItem.priority);
      if (!payload || !source || !priority) return [];

      const value = payload as unknown as WorkflowAttachment;
      if (
        !hasString(value.workflowId) ||
        !Array.isArray(value.selectedNodes) ||
        !Array.isArray(value.haloNodes) ||
        !Array.isArray(value.edges) ||
        !hasNumber(value.tokenEstimate)
      ) {
        return [];
      }
      return [{ ...value, source, priority }];
    }
  );
}

export function codeSelectionsFromMessage(
  item: ChatMessage
): CodeSelectionAttachment[] {
  return contextItems(item, MessageContextItemKind.CODE_SELECTION).flatMap(
    contextItem => {
      const payload = payloadRecord(contextItem);
      const source = contextSourceFromContext(contextItem.source);
      const priority = contextPriorityFromContext(contextItem.priority);
      if (!payload || !source || !priority) return [];

      const value = payload as unknown as CodeSelectionAttachment;
      if (
        !hasString(value.id) ||
        !hasString(value.title) ||
        !hasString(value.surface) ||
        !hasString(value.language) ||
        !hasNumber(value.fromLine) ||
        !hasNumber(value.toLine)
      ) {
        return [];
      }
      return [{ ...value, source, priority }];
    }
  );
}

export function pdfContextAttachmentsFromMessage(
  item: ChatMessage
): PdfContextAttachment[] {
  return contextItems(item, MessageContextItemKind.PDF_BLOCK).flatMap(
    contextItem => {
      const payload = payloadRecord(contextItem);
      const source = contextSourceFromContext(contextItem.source);
      const priority = contextPriorityFromContext(contextItem.priority);
      if (!payload || !source || !priority) return [];

      const value = payload as unknown as PdfContextAttachment;
      if (
        !hasString(value.docId) ||
        !hasString(value.blockId) ||
        !hasString(value.name)
      ) {
        return [];
      }
      return [{ ...value, source, priority }];
    }
  );
}

export function canvasSelectionsFromMessage(
  item: ChatMessage,
  aiChatContextMode: CanvasAIChatContextMode = 'lineage-only'
): CanvasSelectionAttachment[] {
  return contextItems(item, MessageContextItemKind.CANVAS_SELECTION).flatMap(
    contextItem => {
      const payload = payloadRecord(contextItem);
      const source = canvasSourceFromContext(contextItem.source);
      const priority = canvasPriorityFromContext(contextItem.priority);
      if (!payload || !source || !priority) return [];

      const value = payload as unknown as CanvasSelectionAttachment;
      if (
        !hasString(value.docId) ||
        !hasString(value.label) ||
        !Array.isArray(value.elementIds) ||
        !Array.isArray(value.blockIds) ||
        !Array.isArray(value.items)
      ) {
        return [];
      }
      const visibleItems = visibleCanvasSelectionItems(
        value.items,
        aiChatContextMode
      );
      if (!visibleItems.length) return [];
      const flatVisibleItems = flattenCanvasSelectionItems(visibleItems);
      const visibleElementIds = new Set(
        flatVisibleItems
          .filter(item => item.kind === 'canvas_element')
          .map(item => item.id)
      );
      const visibleBlockIds = new Set(
        flatVisibleItems
          .filter(item => item.kind === 'block')
          .map(item => item.id)
      );
      return [
        {
          ...value,
          elementIds: value.elementIds.filter(elementId =>
            visibleElementIds.has(elementId)
          ),
          blockIds: value.blockIds.filter(blockId =>
            visibleBlockIds.has(blockId)
          ),
          items: visibleItems,
          source,
          priority,
        },
      ];
    }
  );
}

export function mailAttachmentsFromMessage(
  item: ChatMessage
): MailMessageAttachment[] {
  return contextItems(item, MessageContextItemKind.MAIL).flatMap(
    contextItem => {
      const payload = payloadRecord(contextItem);
      const source = mailSourceFromContext(contextItem.source);
      const priority = mailPriorityFromContext(contextItem.priority);
      if (!payload || !source || !priority) return [];

      const value = payload as unknown as MailMessageAttachment;
      if (!hasString(value.id)) return [];
      return [{ ...value, source, priority }];
    }
  );
}

export function selectedContextChipsFromMessage(
  item: ChatMessage,
  aiChatContextMode: CanvasAIChatContextMode = 'lineage-only'
): SelectedContextChip[] {
  return contextItems(item, MessageContextItemKind.CANVAS_SELECTION).flatMap(
    (contextItem, index) => {
      const [selection] = canvasSelectionsFromMessage(
        {
          ...item,
          context: {
            ...item.context,
            items: [
              contextItem as NonNullable<
                ChatMessage['context']
              >['items'][number],
            ],
          },
        },
        aiChatContextMode
      );
      if (!selection) return [];
      return [
        {
          uuid: contextItem.id || `${item.id}:canvas:${index}`,
          state: 'finished',
          name: selection.label,
          snapshot: null,
          combinedElementsMarkdown: null,
          html: null,
          canvasSelections: [selection],
        },
      ];
    }
  );
}
